import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaChartLine, FaCalendarAlt, FaYenSign, FaGraduationCap } from 'react-icons/fa';

const MentorProfile = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section className="py-16 md:py-20 bg-gradient-to-b from-darkBlue to-darkerBlue text-white relative overflow-hidden">
      {/* Japanese aesthetic background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-10 text-primary text-[150px] font-bold">師</div>
        <div className="absolute bottom-1/4 right-10 text-primary text-[150px] font-bold">匠</div>
      </div>

      <div className="container max-w-6xl mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <div className="inline-block bg-primary/10 border border-primary/30 rounded-full px-6 py-2 mb-4">
            <span className="text-primary font-semibold">Dibimbing Langsung Oleh</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Master <span className="text-primary">News Trading</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-gradient-to-br from-darkGray/50 to-darkGray/30 rounded-2xl p-8 md:p-12 backdrop-blur-sm border border-primary/20"
        >
          <div className="grid md:grid-cols-3 gap-8 items-center">
            {/* Profile Image & Name */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-center md:text-left"
            >
              <div className="relative inline-block mb-6">
                <div className="w-48 h-48 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-primary/10 border-4 border-primary/40 flex items-center justify-center overflow-hidden">
                  <div className="text-center">
                    <div className="text-6xl font-bold text-primary mb-2">TJ</div>
                    <div className="text-xs text-primary/70">News Specialist</div>
                  </div>
                </div>
                <div className="absolute -bottom-2 -right-2 bg-primary rounded-full p-3">
                  <FaYenSign className="text-2xl text-darkBlue" />
                </div>
              </div>
              
              <h3 className="text-2xl md:text-3xl font-bold mb-2">Tom Jordan</h3>
              <p className="text-primary text-lg">News Trade Specialist</p>
              <p className="text-sm text-textGray mt-2">Est. 2020 • Japan Style Trading</p>
            </motion.div>

            {/* Bio & Philosophy */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="md:col-span-2"
            >
              <div className="mb-6">
                <p className="text-lg md:text-xl leading-relaxed text-textGray mb-4">
                  Memulai perjalanan trading di awal pandemi 2020, Tom Jordan menemukan <span className="text-primary font-semibold">passion</span> dalam menganalisa dampak berita ekonomi terhadap pergerakan market.
                </p>
                <p className="text-lg md:text-xl leading-relaxed text-textGray">
                  Terinspirasi oleh disiplin dan filosofi trader Jepang, Tom mengembangkan metode <span className="text-primary font-semibold">"News Hack"</span> yang menggabungkan analisa fundamental dengan timing presisi ala scalping Jepang.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-darkGray/50 rounded-lg p-4 text-center border border-primary/10"
                >
                  <FaCalendarAlt className="text-2xl text-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold text-primary">5+</div>
                  <div className="text-xs text-textGray">Tahun Pengalaman</div>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-darkGray/50 rounded-lg p-4 text-center border border-primary/10"
                >
                  <FaChartLine className="text-2xl text-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold text-primary">3,000+</div>
                  <div className="text-xs text-textGray">Pips/Tahun</div>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-darkGray/50 rounded-lg p-4 text-center border border-primary/10"
                >
                  <FaGraduationCap className="text-2xl text-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold text-primary">500+</div>
                  <div className="text-xs text-textGray">Murid Aktif</div>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-darkGray/50 rounded-lg p-4 text-center border border-primary/10"
                >
                  <FaYenSign className="text-2xl text-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold text-primary">Ikigai</div>
                  <div className="text-xs text-textGray">Trading Style</div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Quote */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8 pt-8 border-t border-primary/20 text-center"
          >
            <p className="text-xl md:text-2xl italic text-primary font-light">
              "Trading saat news bukan tentang gambling, tapi tentang memahami psikologi market dengan presisi timing."
            </p>
            <p className="text-base text-textGray mt-3">- Tom Jordan</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default MentorProfile;